import { BsPencil } from 'react-icons/bs'
import TableButton from './TableButton'
import { Dropdown, IDropdownItem } from 'components/UI/Dropdown/Dropdown'
import React from 'react'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'

export default function TableButtonAction({
  selectedRowsCount,
  items,
  isDoorsTable,
  entityType,
  disabled,
  emptySelectionLabel,
}: {
  selectedRowsCount: number
  items?: IDropdownItem[]
  isDoorsTable?: boolean
  entityType?: string
  disabled?: boolean
  /** Menu header shown when nothing is selected (plain surfaces only). */
  emptySelectionLabel?: string
} & Partial<React.ComponentProps<typeof TableButton>>) {
  const variant = useTableLinkVariant()
  const hasSelection = selectedRowsCount > 0

  // On default surfaces the bulk Actions button still only appears once rows
  // are selected. On DS2026 (plain) surfaces it lives in the first-column
  // header and now stays persistently visible — when nothing is selected it
  // opens to a disabled menu that prompts the user to make a selection.
  if (!hasSelection && variant !== 'plain') {
    return null
  }

  const defaultEntityType = isDoorsTable ? 'doors' : 'rows'
  const finalEntityType = entityType || defaultEntityType
  const menuLabelText = hasSelection
    ? `Apply to selected ${finalEntityType} (${selectedRowsCount})`
    : (emptySelectionLabel ?? 'Select a target to enable actions')

  // With no selection, keep the trigger clickable (so the prompt is
  // discoverable) but force every action disabled. Strip sub-menus and the
  // per-item disabled tooltips — the menu header already explains why nothing
  // is actionable, and a stale "None of the selected…" tooltip would mislead.
  const menuItems = hasSelection
    ? (items ?? [])
    : (items ?? []).map((item) => ({
        ...item,
        disabled: true,
        subContent: undefined,
        disabledTooltipText: undefined,
      }))

  // Callers pass `disabled={!totalSelectedRows}`, so honour `disabled` only
  // when there is a selection; with none, the trigger must stay clickable.
  const triggerDisabled = hasSelection ? disabled : false

  // h-7 (28px) so the bulk Actions button sits with clear margin inside the
  // 40px DS2026 header row without clipping.
  const plainClassName =
    '!min-h-7 !h-7 !px-3 !text-sm !bg-brand-bright hover:!bg-brand-active'

  return (
    <div data-bulk-actions={variant === 'plain' ? 'true' : undefined}>
      <Dropdown
        className={variant === 'plain' ? plainClassName : 'mt-2'}
        buttonIcon={variant === 'plain' ? undefined : <BsPencil size={20} />}
        buttonLabel="Actions"
        buttonVariant="primary"
        items={menuItems}
        menuLabel={menuLabelText}
        disabled={triggerDisabled}
      />
    </div>
  )
}
